import { useMemo } from 'react';

import { useQuery } from 'react-query';
import { useRecoilValue } from 'recoil';
import styled from 'styled-components';

import { API_URL } from 'api/API';
import getWeatherDataApi from 'api/skyw/getWeatherDataApi';

import { dateTimeState } from 'libs/atoms';
import { MEDIA_SIZE_LIST, WEATHER_HOUR_LIST, WEATHER_HOUR_RANGE } from 'libs/const';
import { DateTime, SkywCommonProps, WeatherData } from 'libs/types';
import { dateToStr } from 'libs/utils';

import Delay from './Delay';
import WeatherGraph from './WeatherGraph';
import UpdateLabel from './UpdateLabel';
import WeatherTable from './WeatherTable';

const WARN_TIME = 10 * 60;

const Container = styled.div``;

const SinceUpdate = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const DelayBox = styled.div`
  width: 80px;
  display: flex;
  align-items: center;
`;

const WeatherLayout = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 10px;
`;

const GraphLoadingWrapper = styled.div`
  position: relative;
  padding-bottom: 80%;
`;

const GraphLoadingBox = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 18px;
  padding-left: 50px;

  @media screen and (max-width: ${`${MEDIA_SIZE_LIST[0]}px`}) {
    padding-left: 40px;
    padding-right: 18px;
  }

  @media screen and (max-width: ${`${MEDIA_SIZE_LIST[1]}px`}) {
    padding: 0;
    margin-top: 20px;
  }
`;

const GraphLoader = styled.div`
  width: 100%;
  height: 100%;
  border: 1px solid #606060;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

function getNowMinDateTime(nowDateTime: DateTime) {
  const nowDate = new Date(`${nowDateTime.date.replaceAll('-', '/')} ${nowDateTime.time}`);
  const minDate = new Date(`${nowDateTime.date.replaceAll('-', '/')} ${nowDateTime.time}`);

  const hourIndex = Math.floor(nowDate.getHours() / 3);

  minDate.setHours(Number(WEATHER_HOUR_LIST[hourIndex]) - WEATHER_HOUR_RANGE);
  minDate.setMinutes(0);
  minDate.setSeconds(0);

  const now = dateToStr(nowDate);
  const min = dateToStr(minDate);

  return {
    now,
    min,
  };
}

const Weather = ({ obsId }: SkywCommonProps) => {
  const dateTime = useRecoilValue(dateTimeState);
  const { data } = useQuery(
    [API_URL.SKYW.GET_WEATHER_DATA, obsId],
    () => {
      const { now, min } = getNowMinDateTime(dateTime!);
      return getWeatherDataApi({ obsId, now, min });
    },
    {
      enabled: !!(dateTime?.obsId === obsId),
      refetchInterval: 1000 * 30,
      refetchIntervalInBackground: true,
      refetchOnWindowFocus: false,
    }
  );

  const latestData = useMemo(() => {
    const t = data?.t_list?.[0] ? data.t_list[data.t_list.length - 1] : (['N/A', 'O'] as WeatherData);
    const h = data?.h_list?.[0] ? data.h_list[data.h_list.length - 1] : (['N/A', 'O'] as WeatherData);
    const c = data?.c_list?.[0] ? data.c_list[data.c_list.length - 1] : (['N/A', 'O'] as WeatherData);
    const w = data?.w_list?.[0] ? data.w_list[data.w_list.length - 1] : (['N/A', 'O'] as WeatherData);
    const time = data?.time_list?.[data.time_list.length - 1];

    return { t, h, c, w, time };
  }, [data]);

  return (
    <Container>
      <SinceUpdate>
        <UpdateLabel>SINCE UPDATE</UpdateLabel>
        <DelayBox>
          <Delay
            obsId={obsId}
            date={data?.latest_update_date_time?.date}
            time={data?.latest_update_date_time?.time}
            warnTime={WARN_TIME}
            isWhite
          />
        </DelayBox>
      </SinceUpdate>
      <WeatherLayout>
        <WeatherTable t={latestData.t} h={latestData.h} c={latestData.c} w={latestData.w} />
        {data ? (
          <WeatherGraph data={data} />
        ) : (
          <GraphLoadingWrapper>
            <GraphLoadingBox>
              <GraphLoader>
                <img src="/assets/skyw/loading.gif" alt="loader" />
              </GraphLoader>
            </GraphLoadingBox>
          </GraphLoadingWrapper>
        )}
      </WeatherLayout>
    </Container>
  );
};

export default Weather;
